import { z } from 'zod';
import { entryCodeSchema } from './reference-tables.js';
import { idSchema, optionalText, requiredText } from './validation.js';
import { NO_INVOICE_DOCUMENTS } from './no-invoice.js';
/** The data a role may require, by label of the table «Ruoli anagrafici». */
export const ROLE_DATA_FIELDS = {
    'Termini di pagamento': 'termsCode',
    'Metodo di pagamento': 'methodCode',
    Priorità: 'priorityCode',
    'Conto riclassificato': 'reclassAccount',
    'Centro di costo': 'costCenter',
    'Conto preferenziale': 'preferredAccount',
    IBAN: 'iban',
};
/** Role data hidden without «Condizioni di pagamento e priorità». */
export const PAYMENT_FIELDS = [
    'termsCode',
    'methodCode',
    'priorityCode',
    'reclassAccount',
    'costCenter',
    'preferredAccount',
];
const optionalCode = z
    .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
    .transform((value) => value ?? null);
const roleDataFields = {
    termsCode: optionalCode,
    methodCode: optionalCode,
    priorityCode: optionalCode,
    reclassAccount: optionalText(100),
    costCenter: optionalText(100),
    preferredAccount: optionalText(100),
    iban: optionalText(40),
};
/** Body of `POST /api/subjects/:id/roles`, and one role of a new subject. */
export const addRoleSchema = z.object({ roleCode: entryCodeSchema, ...roleDataFields });
/** Code of the role in the Excel files of the Tesoreria (C0001, F0001), kept for reference. */
const legacyCode = z
    .string()
    .trim()
    .toUpperCase()
    .max(30, { message: 'Al massimo 30 caratteri' })
    .nullish()
    .transform((value) => value || null);
/** Body of `PATCH /api/subjects/:id/roles/:roleCode`. */
export const updateRoleSchema = z
    .object({
    ...roleDataFields,
    legacyCode,
    active: z.boolean(),
    /** Price lists of a client (proposal Listini §4): empty for the Base. */
    activityListCode: optionalCode,
    costListCode: optionalCode,
    /**
     * With a new «conto riclassificato»: the invoices already registered take
     * it too, except those chosen by hand (1.44).
     */
    reclassToPast: z.boolean(),
})
    .partial()
    .refine((body) => Object.keys(body).length > 0, { message: 'Nessuna modifica' });
const email = z
    .union([z.literal(''), z.email({ message: 'Indirizzo e-mail non valido' })])
    .nullish()
    .transform((value) => value || null);
/** General data of a subject; tax identifiers apart (they have their own rules). */
const subjectFields = {
    typeCode: entryCodeSchema,
    name: requiredText(200),
    country: z.string().regex(/^[A-Z]{2}$/, { message: 'Paese non valido' }),
    address: optionalText(200),
    postalCode: optionalText(12),
    city: optionalText(100),
    province: optionalText(40),
    email,
    pec: email,
    phone: optionalText(40),
    website: optionalText(200),
    sdiCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^([A-Z0-9]{6,7})?$/, { message: 'Codice destinatario: 7 caratteri (6 per la PA)' })
        .nullish()
        .transform((value) => value || null),
    notes: optionalText(2000),
    gestifyId: optionalText(40),
    /** Self-billing or reverse charge, for every role of the subject. */
    selfInvoicing: z.boolean().default(false),
    /** 1.000 employees or more: the first contract with it is complex (allegato B §5). */
    largeCompany: z.boolean().default(false),
    /** It never invoices (3.14): an insurance, a consorzio di garanzia, a Comune for fines. */
    /** A shareholder or a company linked to one: its invoices may be intergroup (3.18). */
    linkedCompany: z.boolean().default(false),
    noInvoice: z.boolean().default(false),
    noInvoiceDocument: z
        .enum(NO_INVOICE_DOCUMENTS)
        .nullish()
        .transform((value) => value ?? null),
};
const taxIdFields = {
    vatNumber: optionalText(30),
    taxCode: optionalText(30),
};
/** Body of `POST /api/subjects`: the subject with at least one role. */
export const createSubjectSchema = z.object({
    ...subjectFields,
    ...taxIdFields,
    /**
     * The identifier is not at hand: the subject is entered anyway, stays «da
     * completare» and cannot be used to issue a document (proposal §2.3).
     */
    withoutTaxId: z.boolean().default(false),
    roles: z.array(addRoleSchema).min(1, { message: 'Scegli almeno un ruolo' }),
    classifications: z.record(z.string(), entryCodeSchema).default({}),
});
/**
 * Body of `PATCH /api/subjects/:id`. Tax identifiers change only with the
 * Amministrazione level of Anagrafica (proposal §2.3).
 */
export const updateSubjectSchema = z
    .object({ ...subjectFields, ...taxIdFields })
    .partial()
    .refine((body) => Object.keys(body).length > 0, { message: 'Nessuna modifica' });
/** Body of `PUT /api/subjects/:id/classifications`: table key → entry code; missing means none. */
export const classificationsSchema = z.record(z.string(), entryCodeSchema);
export const contactSchema = z.object({
    name: requiredText(120),
    title: optionalText(120),
    email,
    phone: optionalText(40),
    isPrimary: z.boolean().default(false),
});
export const aliasesSchema = z.object({
    aliases: z.array(requiredText(200)).max(20, { message: 'Al massimo 20 alias' }),
});
/** Body of `POST /api/subjects/tax-id-check` and of an unlock request. */
export const taxIdCheckSchema = z.object({
    country: z.string().regex(/^[A-Z]{2}$/),
    typeCode: entryCodeSchema,
    ...taxIdFields,
});
export const unlockRequestSchema = taxIdCheckSchema.extend({ message: optionalText(500) });
export const unlockDecisionSchema = z.object({ approve: z.boolean() });
export const ownerSchema = z.object({ ownerId: idSchema });
export const shareSchema = z.object({ userId: idSchema });
export const activeSchema = z.object({ active: z.boolean() });
/**
 * The source of an import: the export file of Gestify, base64; or its rows
 * already read by the browser from the Gestify API (decision of 05/10/2026),
 * with the headings of the file (`gestifyApiRow`).
 */
const importSourceSchema = z.object({
    file: z.string().min(1, { message: 'Scegli un file' }).max(16_000_000).optional(),
    rows: z
        .array(z.record(z.string().max(60), z.string().max(500).nullable()))
        .min(1, { message: 'Gestify non ha dato nessuna anagrafica' })
        .max(5000, { message: 'Al massimo 5000 righe per volta' })
        .optional(),
});
const oneSource = (body) => (body.file === undefined) !== (body.rows === undefined);
/** Body of `POST /api/subjects/import/preview`: the file or the rows. */
export const importPreviewSchema = importSourceSchema.refine(oneSource, {
    message: 'Scegli un file',
    path: ['file'],
});
/** Body of `POST /api/subjects/import`: the same source and the rows to import. */
export const importApplySchema = importSourceSchema
    .extend({
    lines: z.array(z.number().int().positive()).min(1, { message: 'Scegli almeno una riga' }),
})
    .refine(oneSource, { message: 'Scegli un file', path: ['file'] });
//# sourceMappingURL=registry-contracts.js.map